import { createContext, type PropsWithChildren, useContext } from 'react';

import {
  type CollectionProps,
  type PermissionMode,
  type ShortUrlProps,
} from '~/components/SharePrivateCollectionModal/types';

export type SharePrivateCollectionModalContextValue = {
  // State
  collectionProps: CollectionProps;
  shortUrlProps: ShortUrlProps;
  isSaving: boolean;
  isEditMode: boolean;
  errors: Record<string, string> | null;
  selectedItemCount?: number;
  hasCreatedShareLink?: boolean;

  // Update functions
  updateCollectionName: (name: string) => void;
  updateLinkAccessPermission: (value: boolean) => void;
  updateAssetPermission: <K extends keyof ShortUrlProps['permissions']['asset']>(
    key: K,
    value: ShortUrlProps['permissions']['asset'][K],
  ) => void;
  updateCommentPermissions: (mode: PermissionMode) => void;
  updateCustomFieldPermissions: (mode: PermissionMode) => void;
  updatePasscode: (passcode: string | null) => void;
  updateExpirationDate: (expirationDate: string | null) => void;

  // Save functions (optional in create mode)
  saveCollectionName?: () => void;
  savePasscode?: (value?: string | null) => void;
  saveExpirationDate?: (value?: string | null) => void;

  // Actions
  onSave: () => void;
  onClose: () => void;
};

const SharePrivateCollectionModalContext = createContext<SharePrivateCollectionModalContextValue | null>(null);

export type SharePrivateCollectionModalProviderProps = PropsWithChildren<{
  value: SharePrivateCollectionModalContextValue;
}>;

export const SharePrivateCollectionModalProvider = ({ children, value }: SharePrivateCollectionModalProviderProps) => {
  return (
    <SharePrivateCollectionModalContext.Provider value={value}>{children}</SharePrivateCollectionModalContext.Provider>
  );
};

export const useSharePrivateCollectionModal = () => {
  const context = useContext(SharePrivateCollectionModalContext);

  if (!context) {
    throw new Error('useSharePrivateCollectionModal must be used within SharePrivateCollectionModalProvider');
  }

  return context;
};
